import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Field, Modal, Text } from '../../../../components';
import { useDebouncedValue } from '../../useDebouncedValue';
import { pathErrorCopy } from '../../seo-copy';
import type { SiteSeoPage } from '../../seo-types';
import { validatePath, validateWildcardPrefix } from '../../seo-validation';
import { SEARCH_DEBOUNCE_MS, UrlPicker, type SearchUrls } from '../UrlPicker';
import { Actions, Body, ErrorText, Hint, ModeButton, Modes } from './AddRouteDialog.styles';

export type AddRouteDialogProps = {
  open: boolean;
  /** The draft's paths. */
  existing: string[];
  pages: SiteSeoPage[];
  /** The site's real urls: the page picker, and the check that some page starts with a section's prefix. */
  searchUrls: SearchUrls;
  onClose: () => void;
  onAdd: (path: string) => void;
};

type Mode = 'page' | 'section';

/** The server's answer on whether some page starts with `prefix`. */
type PrefixCheck = { prefix: string; outcome: 'pages' | 'none' | 'failed' };

/**
 * Add route, in two modes. "A page": one of the site's real urls, picked from a search (no free text). "A section
 * (footer only)": a footer wildcard (`/casino/*`); the hub's own check ({@link validatePath}) names what is wrong
 * under the field as the owner types, then the server is asked whether any page starts with the prefix. Add stays
 * disabled until a row is picked, or the section is valid and has pages.
 */
export const AddRouteDialog = ({
  open,
  existing,
  pages,
  searchUrls,
  onClose,
  onAdd,
}: AddRouteDialogProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const [mode, setMode] = React.useState<Mode>('page');
  const [picked, setPicked] = React.useState<string | null>(null);
  const [value, setValue] = React.useState('');
  const [check, setCheck] = React.useState<PrefixCheck | null>(null);
  const exclude = React.useMemo(() => new Set(existing), [existing]);
  // The caller may pass a new function each render; only a new prefix asks the server again.
  const searchRef = React.useRef(searchUrls);
  searchRef.current = searchUrls;

  const path = value.trim();
  const localError = path === '' ? null : validatePath(path, existing, pages);
  const prefix = localError === null ? validateWildcardPrefix(path) : null;
  const checkedPrefix = useDebouncedValue(mode === 'section' ? prefix : null, SEARCH_DEBOUNCE_MS);

  React.useEffect(() => {
    if (checkedPrefix === null) return undefined;
    const controller = new AbortController();
    searchRef
      .current({ prefix: checkedPrefix, limit: 1 }, controller.signal)
      .catch(() => ({ ok: false as const, reason: 'failed' as const }))
      .then((result) => {
        if (controller.signal.aborted) return;
        const outcome = !result.ok ? 'failed' : result.value.total > 0 ? 'pages' : 'none';
        setCheck({ prefix: checkedPrefix, outcome });
      });
    return () => controller.abort();
  }, [checkedPrefix]);

  const answered = prefix !== null && check !== null && check.prefix === prefix && checkedPrefix === prefix;
  const error = localError ?? (answered && check.outcome === 'none' ? 'path-no-page' : null);
  const checkFailed = answered && check.outcome === 'failed';
  const sectionValid = prefix !== null && answered && check.outcome === 'pages';
  const target = mode === 'page' ? picked : sectionValid ? path : null;

  const reset = (): void => {
    setMode('page');
    setPicked(null);
    setValue('');
    setCheck(null);
  };
  const close = (): void => {
    reset();
    onClose();
  };
  const add = (): void => {
    if (target === null) return;
    reset();
    onAdd(target);
  };
  const modeOption = (id: Mode, label: string): React.ReactElement => (
    <ModeButton
      type="button"
      role="radio"
      aria-checked={mode === id}
      $active={mode === id}
      onClick={() => setMode(id)}
    >
      <Text text={label} raw tag="span" />
    </ModeButton>
  );

  return (
    <Modal
      open={open}
      title={t('Add route')}
      onClose={close}
      footer={
        <Actions>
          <Button label={t('Cancel')} variant="outline" onClick={close} />
          <Button label={t('Add route')} disabled={target === null} onClick={add} />
        </Actions>
      }
    >
      <Body>
        <Modes role="radiogroup" aria-label={t('What to add')}>
          {modeOption('page', t('A page'))}
          {modeOption('section', t('A section (footer only)'))}
        </Modes>
        {mode === 'page' ? (
          <UrlPicker
            searchUrls={searchUrls}
            exclude={exclude}
            selected={picked ?? undefined}
            onPick={(url) => setPicked(url.path)}
          />
        ) : (
          <>
            <Field label={t('Route')} value={value} onChange={setValue} placeholder="/casino/*" />
            {error ? <ErrorText text={pathErrorCopy(t, error, path)} raw tag="span" /> : null}
            {checkFailed ? <ErrorText text="Couldn't search right now" tag="span" /> : null}
            <Hint text="/casino/* means every page under Casino and only sets their footer text." tag="span" />
          </>
        )}
      </Body>
    </Modal>
  );
};

export default AddRouteDialog;
